/* Copyright (c) 2025, NVIDIA CORPORATION.  All rights reserved.
 *
 * NVIDIA CORPORATION and its licensors retain all intellectual property
 * and proprietary rights in and to this software, related documentation
 * and any modifications thereto.  Any use, reproduction, disclosure or
 * distribution of this software and related documentation without an express
 * license agreement from NVIDIA CORPORATION is strictly prohibited.
 */

import { Provider, NgZone, Injector } from '@angular/core';
import { BridgeService } from 'crimson/lib/bridge/bridge.service';
import { BrowserLeanModeService } from 'crimson/lib/browser-lean-mode-service/browser-lean-mode.service';
import { LoggingService } from 'crimson/lib/logging/logging.service';
import { SystemInfoService } from 'crimson/lib/system-info-service/system-info.service';
import { BrowserSupportService } from 'browser-support/lib/browser-support.service';
import { AndroidGamepadService } from './android-gamepad-service';
import { NvGamepadConfig } from './gamepad.config';
import { GamepadService } from './gamepad.service';
import { GeronimoEventGamepadService } from './geronimo-event-gamepad.service';
import { NoOpGamepadService } from './no-op-gamepad.service';
import { WebApiGamepadService } from './web-api-gamepad.service';

export function gamepadServiceCreator(
    gamepadConfig: NvGamepadConfig,
    ngZone: NgZone,
    injector: Injector
): GamepadService {
    switch (gamepadConfig.implementationType) {
        case 'android':
            const systemInfoService = injector.get(SystemInfoService);
            return new AndroidGamepadService(systemInfoService);
        case 'geronimo': {
            const loggingService = injector.get(LoggingService);
            const bridgeService = injector.get(BridgeService);
            return new GeronimoEventGamepadService(loggingService, bridgeService, gamepadConfig, ngZone);
        }
        case 'webapi': {
            const loggingService = injector.get(LoggingService);
            const leanModeService = injector.get(BrowserLeanModeService);
            const browserSupportService = injector.get(BrowserSupportService);
            return new WebApiGamepadService(
                loggingService,
                leanModeService,
                ngZone,
                browserSupportService,
                gamepadConfig
            );
        }
        case 'no-op':
            return new NoOpGamepadService();
    }
}

export const GamepadServiceProvider: Provider = {
    provide: GamepadService,
    useFactory: gamepadServiceCreator,
    deps: [NvGamepadConfig, NgZone, Injector]
};
